import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

const PLACEHOLDER_COUNT = 5;

export const TopProductsSkeleton: React.FC = () => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('sales.widgets.topProducts.title'),
        }),
        [t],
    );

    return (
        <section
            aria-busy
            aria-labelledby="top-products-widget-title"
            {...getNodeTestIdProp('top-products-section')}
        >
            <Widget
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('top-products-widget')}
            >
                <Widget.Header
                    className="mt-4"
                    {...getNodeTestIdProp('top-products-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('top-products-widget-title')}
                    >
                        <Skeleton
                            className="size-8 shrink-0 rounded-lg"
                            {...getNodeTestIdProp('top-products-icon-skeleton')}
                        />
                        {copy.title}
                    </Widget.Title>
                    <Widget.Description
                        {...getNodeTestIdProp('top-products-widget-subtitle')}
                    >
                        <Skeleton
                            className="h-3 w-32 rounded-md"
                            {...getNodeTestIdProp(
                                'top-products-widget-subtitle-skeleton',
                            )}
                        />
                    </Widget.Description>
                </Widget.Header>
                <Widget.Content>
                    <ul
                        aria-label={copy.title}
                        className="-mr-2 flex max-h-88 flex-col gap-3 overflow-y-auto rounded-md pr-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                        tabIndex={0}
                        {...getNodeTestIdProp('top-products-list')}
                    >
                        {Array.from({ length: PLACEHOLDER_COUNT }).map(
                            (_, index) => {
                                const discriminator = `placeholder-${index}`;
                                const isLast = index === PLACEHOLDER_COUNT - 1;
                                return (
                                    <li
                                        key={discriminator}
                                        className="flex flex-col gap-1.5"
                                        {...getNodeTestIdProp(
                                            `top-products-item-${discriminator}`,
                                        )}
                                    >
                                        <div
                                            className="flex items-center gap-3"
                                            {...getNodeTestIdProp(
                                                `top-products-item-root-${discriminator}`,
                                            )}
                                        >
                                            <div
                                                aria-hidden
                                                className="flex w-7 shrink-0 items-center justify-center"
                                                {...getNodeTestIdProp(
                                                    `top-products-item-rank-${discriminator}`,
                                                )}
                                            >
                                                <Skeleton
                                                    className="size-4 rounded-md"
                                                    {...getNodeTestIdProp(
                                                        `top-products-item-rank-skeleton-${discriminator}`,
                                                    )}
                                                />
                                            </div>
                                            <div
                                                className="flex min-w-0 flex-1 flex-col gap-1.5"
                                                {...getNodeTestIdProp(
                                                    `top-products-item-details-root-${discriminator}`,
                                                )}
                                            >
                                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                                    <div className="flex min-w-0 flex-col">
                                                        <Skeleton
                                                            className="h-3.5 w-48 rounded-md"
                                                            {...getNodeTestIdProp(
                                                                `top-products-item-name-${discriminator}`,
                                                            )}
                                                        />
                                                    </div>
                                                    <div
                                                        className="flex shrink-0 items-end flex-row sm:flex-row-reverse sm:gap-2 sm:items-center"
                                                        {...getNodeTestIdProp(
                                                            `top-products-item-meta-root-${discriminator}`,
                                                        )}
                                                    >
                                                        <Skeleton
                                                            className="h-3.5 w-16 rounded-md"
                                                            {...getNodeTestIdProp(
                                                                `top-products-item-revenue-${discriminator}`,
                                                            )}
                                                        />
                                                        <Skeleton
                                                            className="ml-auto h-2.5 w-24 rounded-md"
                                                            {...getNodeTestIdProp(
                                                                `top-products-item-meta-${discriminator}`,
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                                <div
                                                    className="flex items-center gap-2"
                                                    {...getNodeTestIdProp(
                                                        `top-products-item-progress-root-${discriminator}`,
                                                    )}
                                                >
                                                    <Skeleton
                                                        className="h-1.5 flex-1 rounded-full"
                                                        {...getNodeTestIdProp(
                                                            `top-products-item-progress-${discriminator}`,
                                                        )}
                                                    />
                                                    <Skeleton
                                                        className="h-2.5 w-8 rounded-md"
                                                        {...getNodeTestIdProp(
                                                            `top-products-item-progress-output-${discriminator}`,
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        {isLast ? null : (
                                            <div
                                                aria-hidden
                                                className="ml-10 border-b border-default-100"
                                                {...getNodeTestIdProp(
                                                    `top-products-item-separator-${discriminator}`,
                                                )}
                                            />
                                        )}
                                    </li>
                                );
                            },
                        )}
                    </ul>
                </Widget.Content>
            </Widget>
        </section>
    );
};
